/**
 * ADR-0235 — load a site's trust verdicts once per (site, window). Returns
 * `null` while the flag is off, the API is unreachable, or no HTTP
 * provider is mounted (legacy test scaffolds): callers then render exactly
 * what they rendered before.
 */

import { useCallback, useEffect, useState } from 'react';

import type { MerchantAgentDisputeRef, MerchantAgentVerdict, TrustVerdictRange } from '@sill/types/api';

import { useOptionalHttp } from '../../../api/http-context.js';
import { getTrustVerdicts } from '../../../api/trust-verdicts.js';

export interface TrustVerdictsHandle {
  readonly agents: readonly MerchantAgentVerdict[];
  /** Record a dispute the user just filed so the chip updates without a refetch. */
  readonly addDispute: (claim_key: string, dispute: MerchantAgentDisputeRef) => void;
}

export function useTrustVerdicts(site_id: string | null, range: TrustVerdictRange): TrustVerdictsHandle | null {
  const http = useOptionalHttp();
  const [agents, setAgents] = useState<readonly MerchantAgentVerdict[] | null>(null);

  useEffect(() => {
    setAgents(null);
    if (http === null || site_id === null) return;
    let cancelled = false;
    void getTrustVerdicts(http, site_id, range).then((s) => {
      if (!cancelled) setAgents(s.kind === 'live' ? s.agents : null);
    });
    return () => { cancelled = true; };
  }, [http, site_id, range]);

  const addDispute = useCallback((claim_key: string, dispute: MerchantAgentDisputeRef): void => {
    setAgents((cur) => cur === null ? cur : cur.map((a) => (a.claim_key === claim_key ? { ...a, disputes: [dispute, ...a.disputes] } : a)));
  }, []);

  return agents === null ? null : { agents, addDispute };
}
